/**
 * Pallet Group Assignment API client.
 * Read endpoints call the pallet-group service; Assign / Unassign / Reassign / Complete
 * POST to `/api/node/wmextn/pallet_group/update/v1` when not using mock data.
 */

import { axios } from "../utilities/http";
import config from "../config/config";
import {
  MOCK_UNASSIGNED_PALLET_GROUPS,
  MOCK_SPUR_SUMMARY,
} from "../data/mockPalletGroupData";
import { resolveIlpnIdsForRow } from "../components/PalletGroup/pgIlpnUtils";
import {
  applyAssignPalletGroupsToSpurs,
  applyReassignToSpurs,
  applyUnassignFromSpur,
  createInitialSpurAssignments,
  getAssignedPalletGroupsForSpur,
  mapUnassignedRowToSpurPg,
} from "../components/PalletGroup/pgSpurAssignmentUtils";
import { SPUR_DISPLAY_STATUS } from "../constants/spurDisplayStatus";
import { buildShipToFields, buildSpurSummaryShipToFields } from "../components/PalletGroup/pgShipToUtils";

function mapUnassignedPalletGroupToGridRow(detail) {
  const poIds = detail.purchase_order_ids || [];
  const shipTo = buildShipToFields(detail.ship_to_id);
  return {
    id: detail.pallet_group_id,
    palletGroup: detail.pallet_group_id,
    type: detail.pallet_type || "—",
    inboundId: detail.inbound_id || "—",
    asn: detail.asn_id || "—",
    ...shipTo,
    po: poIds.length > 0 ? poIds.join(", ") : "—",
    pgType: detail.pallet_type || "—",
    totalILPNs: detail.total_ilpns ?? 0,
    sortingRule: "Std palletise",
    ilpnIds: [],
    diverted: detail.diverted ?? 0,
    palletised: detail.palletised ?? 0,
  };
}

function mapSpurDetailToGridRow(detail, index) {
  const pgIds = detail.pallet_group_ids || [];
  const palletGroup = detail.pallet_group || "—";
  const readyToComplete =
    detail.status === SPUR_DISPLAY_STATUS.READY_TO_COMPLETE;
  const shipToFields = buildSpurSummaryShipToFields(detail.ship_to);
  return {
    id: detail.spur || `spur-${index}`,
    spur: detail.spur,
    spurType: detail.spur_type,
    spurStatus: readyToComplete
      ? SPUR_DISPLAY_STATUS.ASSIGNED
      : detail.status,
    palletGroup,
    palletGroupCount:
      pgIds.length > 0 ? pgIds.length : palletGroup !== "—" ? 1 : 0,
    palletGroupIds: pgIds,
    inboundId: detail.inbound_id || "—",
    totalCartons: detail.total_cartons ?? 0,
    diverted: detail.diverted ?? 0,
    palletised: detail.palletised ?? 0,
    receivingZone: detail.receiving_zone,
    ...shipToFields,
    readyToComplete,
  };
}

function buildFilterParams(siteCode, extraFilters = []) {
  return {
    params: { filters: [`destLocCode(${siteCode})`, ...extraFilters] },
    paramsSerializer: { indexes: null },
  };
}

function buildSpurFilterParams(siteCode, filters = {}, pagination = {}) {
  const extra = [];
  const { receivingZone, spur, inboundId, spurStatus } = filters;
  const { page = 1, pageSize = 15 } = pagination;
  if (receivingZone) {
    extra.push(`receivingZone(${receivingZone})`);
  }
  if (spur) {
    extra.push(`spur(${spur})`);
  }
  if (inboundId) {
    extra.push(`inboundId(${inboundId})`);
  }
  if (spurStatus) {
    extra.push(`status(${spurStatus})`);
  }
  const base = buildFilterParams(siteCode, extra);
  return {
    ...base,
    params: {
      ...base.params,
      page,
      pageSize,
    },
  };
}

async function fetchSpurSummaryFromService(siteCode, filters = {}, pagination = {}) {
  const url = `${config.palletGroupApi}/api/node/wmextn/pallet_group/spur_details/v1`;
  const { data } = await axios.get(
    url,
    buildSpurFilterParams(siteCode, filters, pagination),
  );
  const list = data.spurDetailList || [];
  const catalogList = data.spurDetailListTotal || list;
  return {
    rows: list.map(mapSpurDetailToGridRow),
    catalogRows: catalogList.map(mapSpurDetailToGridRow),
    totalCount: data.total_count ?? list.length,
    page: data.page ?? pagination.page ?? 1,
    pageSize: data.page_size ?? pagination.pageSize ?? 15,
  };
}

function filterMockSpurRows(rows, filters = {}) {
  const { receivingZone, spur, inboundId, spurStatus } = filters;
  return rows.filter((row) => {
    if (receivingZone && row.receivingZone !== receivingZone) {
      return false;
    }
    if (spur && row.spur !== spur) {
      return false;
    }
    if (
      inboundId &&
      row.inboundId.toLowerCase() !== inboundId.toLowerCase()
    ) {
      return false;
    }
    if (spurStatus) {
      if (spurStatus === SPUR_DISPLAY_STATUS.READY_TO_COMPLETE) {
        if (!row.readyToComplete) return false;
      } else if (spurStatus === SPUR_DISPLAY_STATUS.ASSIGNED) {
        if (
          row.spurStatus !== SPUR_DISPLAY_STATUS.ASSIGNED ||
          row.readyToComplete
        ) {
          return false;
        }
      } else if (row.spurStatus !== spurStatus) {
        return false;
      }
    }
    return true;
  });
}

/**
 * @param {{ siteCode?: string, receivingZone?: string, spur?: string, inboundId?: string, spurStatus?: string, page?: number, pageSize?: number }} params
 */
export async function fetchSpurSummary(params = {}) {
  const {
    siteCode,
    receivingZone,
    spur,
    inboundId,
    spurStatus,
    page = 1,
    pageSize = 15,
  } = params;
  const useMockOnly = process.env.REACT_APP_USE_MOCK_DATA === "true";
  const filters = { receivingZone, spur, inboundId, spurStatus };
  const pagination = { page, pageSize };

  if (useMockOnly || !siteCode) {
    const filtered = filterMockSpurRows(MOCK_SPUR_SUMMARY, filters);
    return {
      ...paginateRows(filtered, page, pageSize),
      catalogRows: MOCK_SPUR_SUMMARY,
    };
  }

  return fetchSpurSummaryFromService(siteCode, filters, pagination);
}

function paginateRows(rows, page = 1, pageSize = 15) {
  const totalCount = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    rows: rows.slice(start, start + pageSize),
    catalogRows: rows,
    totalCount,
    page: safePage,
    pageSize,
  };
}

function buildUnassignedPgFilterParams(siteCode, filters = {}, pagination = {}) {
  const extra = [];
  const { inboundId, asnId, palletType, palletGroupId } = filters;
  const { page = 1, pageSize = 15 } = pagination;
  if (inboundId) {
    extra.push(`inboundId(${inboundId})`);
  }
  if (asnId) {
    extra.push(`asnId(${asnId})`);
  }
  if (palletType) {
    extra.push(`palletType(${palletType})`);
  }
  if (palletGroupId) {
    extra.push(`palletGroupId(${palletGroupId})`);
  }
  const base = buildFilterParams(siteCode, extra);
  return {
    ...base,
    params: {
      ...base.params,
      page,
      pageSize,
    },
  };
}

async function fetchUnassignedPalletGroupsFromService(siteCode, filters = {}, pagination = {}) {
  const url = `${config.palletGroupApi}/api/node/wmextn/pallet_group/unassigned_pallet_groups/v1`;
  const { data } = await axios.get(
    url,
    buildUnassignedPgFilterParams(siteCode, filters, pagination),
  );
  const list = data.unassigned_pallet_group_list || [];
  return {
    rows: list.map(mapUnassignedPalletGroupToGridRow),
    totalCount: data.total_count ?? list.length,
    page: data.page ?? pagination.page ?? 1,
    pageSize: data.page_size ?? pagination.pageSize ?? 15,
  };
}

function filterMockUnassignedRows(rows, filters = {}) {
  const { inboundId, asnId, palletType, palletGroupId } = filters;
  return rows.filter((row) => {
    if (palletGroupId && row.palletGroup !== palletGroupId) {
      return false;
    }
    if (asnId && row.asn.toLowerCase() !== asnId.toLowerCase()) {
      return false;
    }
    if (palletType && row.pgType !== palletType) {
      return false;
    }
    if (inboundId && row.inboundId.toLowerCase() !== inboundId.toLowerCase()) {
      return false;
    }
    return true;
  });
}

/**
 * @param {{ siteCode?: string, inboundId?: string, asnId?: string, palletType?: string, palletGroupId?: string, page?: number, pageSize?: number }} params
 * @returns {Promise<{ rows: Array, totalCount: number, page: number, pageSize: number }>}
 */
export async function fetchUnassignedPalletGroups(params = {}) {
  const {
    siteCode,
    inboundId,
    asnId,
    palletType,
    palletGroupId,
    page = 1,
    pageSize = 15,
  } = params;
  const useMockOnly = process.env.REACT_APP_USE_MOCK_DATA === "true";
  const filters = { inboundId, asnId, palletType, palletGroupId };
  const pagination = { page, pageSize };

  if (useMockOnly || !siteCode) {
    const filtered = filterMockUnassignedRows(MOCK_UNASSIGNED_PALLET_GROUPS, filters);
    return paginateRows(filtered, page, pageSize);
  }

  return fetchUnassignedPalletGroupsFromService(siteCode, filters, pagination);
}

function cloneWorkspaceState(state) {
  return {
    unassignedPalletGroups: [...(state.unassignedPalletGroups || [])],
    spurSummary: [...(state.spurSummary || [])],
    spurAssignments: JSON.parse(JSON.stringify(state.spurAssignments || {})),
  };
}

function shouldUseMockAssignmentActions(siteCode) {
  return process.env.REACT_APP_USE_MOCK_DATA === "true" || !siteCode;
}

const SPUR_UPDATE_URL = `${config.palletGroupApi}/api/node/wmextn/pallet_group/update/v1`;

function buildSpurUpdatePayload({
  action,
  siteCode,
  modifiedBy,
  palletGroupIds,
  spurIds,
  newSpurIds,
  forceComplete,
}) {
  const payload = {
    pallet_group: palletGroupIds,
    spur: spurIds,
    destination_location_code: siteCode,
    action,
    modified_by: modifiedBy,
  };
  if (newSpurIds?.length) {
    payload.new_spur = newSpurIds;
  }
  if (forceComplete) {
    payload.force_complete = true;
  }
  return payload;
}

async function postSpurUpdate(payload) {
  const { data } = await axios.post(SPUR_UPDATE_URL, payload);
  if (!data?.update_successful) {
    const err = new Error("Pallet group update did not succeed.");
    err.code = "UPDATE_NOT_SUCCESSFUL";
    throw err;
  }
  return data;
}

/**
 * @typedef {object} AssignmentWorkspace
 * @property {Array} unassignedPalletGroups
 * @property {Array} spurSummary
 * @property {Record<string, Array>} spurAssignments
 */

/**
 * Load unassigned PGs, spur summary, and spur assignment map.
 * @param {{ siteCode?: string }} [params]
 * @returns {Promise<AssignmentWorkspace>}
 */
export async function fetchAssignmentWorkspace(params = {}) {
  const { siteCode } = params;
  const useMockOnly = process.env.REACT_APP_USE_MOCK_DATA === "true";

  if (useMockOnly || !siteCode) {
    return {
      unassignedPalletGroups: [...MOCK_UNASSIGNED_PALLET_GROUPS],
      spurSummary: [...MOCK_SPUR_SUMMARY],
      spurCatalogRows: [...MOCK_SPUR_SUMMARY],
      spurAssignments: createInitialSpurAssignments(),
    };
  }

  const [spurResult, unassignedResult] = await Promise.all([
    fetchSpurSummary({ siteCode, page: 1, pageSize: 15 }),
    fetchUnassignedPalletGroups({ siteCode, page: 1, pageSize: 15 }),
  ]);

  return {
    unassignedPalletGroups: unassignedResult.rows,
    unassignedTotalCount: unassignedResult.totalCount,
    spurSummary: spurResult.rows,
    spurCatalogRows: spurResult.catalogRows ?? spurResult.rows,
    spurTotalCount: spurResult.totalCount,
    spurAssignments: createInitialSpurAssignments(),
  };
}

function mapAssignedPalletGroupDetailToModalRow(detail) {
  return mapUnassignedRowToSpurPg(mapUnassignedPalletGroupToGridRow(detail));
}

async function fetchSpurAssignedPalletGroupsFromService(siteCode, spurId) {
  const url = `${config.palletGroupApi}/api/node/wmextn/pallet_group/spur_assigned_pallet_groups/v1`;
  const { data } = await axios.get(
    url,
    buildFilterParams(siteCode, [`spur(${spurId})`]),
  );
  const list = data.assigned_pallet_group_list || [];
  return list.map(mapAssignedPalletGroupDetailToModalRow);
}

/**
 * @param {{ siteCode?: string, spurId: string, spurRow: object, spurAssignments: object }} params
 */
export async function fetchSpurAssignedPalletGroups(params) {
  const { siteCode, spurId, spurRow, spurAssignments } = params;
  const useMockOnly = process.env.REACT_APP_USE_MOCK_DATA === "true";

  if (!useMockOnly && siteCode && spurId) {
    return fetchSpurAssignedPalletGroupsFromService(siteCode, spurId);
  }

  return getAssignedPalletGroupsForSpur(spurRow, spurAssignments);
}

/**
 * @param {{ palletGroupId: string, palletGroupRow: object, includeCartonStatus?: boolean }} params
 */
export async function fetchPalletGroupIlpns(params) {
  const {
    palletGroupId,
    palletGroupRow,
    includeCartonStatus = false,
    siteCode,
  } = params;
  const useMockOnly = process.env.REACT_APP_USE_MOCK_DATA === "true";

  if (useMockOnly || !siteCode) {
    const row = palletGroupRow || {};
    const ilpnIds = resolveIlpnIdsForRow(row);
    return {
      ilpnIds,
      diverted: includeCartonStatus ? row.diverted ?? 0 : undefined,
      palletised: includeCartonStatus ? row.palletised ?? 0 : undefined,
      totalILPNs: row.totalILPNs ?? ilpnIds.length,
    };
  }

  const url = `${config.palletGroupApi}/api/node/wmextn/pallet_group/pallet_group_ilpns/v1`;
  const { data } = await axios.get(
    url,
    buildFilterParams(siteCode, [`palletGroupId(${palletGroupId})`]),
  );
  const ilpnList = data.ilpn_list || [];
  const ilpnIds = ilpnList.map((item) => item.ilpn_id).filter(Boolean);

  return {
    ilpnIds,
    ilpnDetails: ilpnList,
    diverted: includeCartonStatus ? data.diverted_count ?? 0 : undefined,
    palletised: includeCartonStatus ? data.palletised_count ?? 0 : undefined,
    totalILPNs: data.total_ilpns ?? ilpnIds.length,
  };
}

/**
 * @param {object} params
 * @param {AssignmentWorkspace} params.workspace
 * @param {Array} params.palletGroupsToAssign
 * @param {string[]} params.targetSpurKeys
 */
export async function assignPalletGroups(params) {
  const {
    workspace,
    palletGroupsToAssign,
    targetSpurKeys,
    siteCode,
    modifiedBy,
  } = params;

  if (!shouldUseMockAssignmentActions(siteCode)) {
    const palletGroupIds = palletGroupsToAssign.map(
      (row) => row.id || row.palletGroup,
    );
    await postSpurUpdate(
      buildSpurUpdatePayload({
        action: "Assign",
        siteCode,
        modifiedBy,
        palletGroupIds,
        spurIds: targetSpurKeys,
      }),
    );
    const refreshed = await fetchAssignmentWorkspace({ siteCode });
    return {
      ok: true,
      workspace: refreshed,
      assignedIds: palletGroupIds,
    };
  }

  const result = applyAssignPalletGroupsToSpurs({
    assignmentsBySpur: workspace.spurAssignments,
    spurRows: workspace.spurSummary,
    palletGroupsToAssign,
    targetSpurKeys,
  });

  if (!result) {
    return { ok: false, error: "INVALID_ASSIGNMENT" };
  }

  const assignedIdSet = new Set(result.assignedIds);
  const nextWorkspace = {
    spurAssignments: result.assignmentsBySpur,
    spurSummary: result.spurRows,
    unassignedPalletGroups: workspace.unassignedPalletGroups.filter(
      (row) => !assignedIdSet.has(row.id),
    ),
  };

  return { ok: true, workspace: nextWorkspace, assignedIds: result.assignedIds };
}

/**
 * @param {object} params
 * @param {AssignmentWorkspace} params.workspace
 * @param {string} params.sourceSpurKey
 * @param {string} params.targetSpurKey
 * @param {string[]} params.pgIds
 */
export async function reassignPalletGroups(params) {
  const {
    workspace,
    sourceSpurKey,
    targetSpurKey,
    pgIds,
    siteCode,
    modifiedBy,
  } = params;

  if (!shouldUseMockAssignmentActions(siteCode)) {
    await postSpurUpdate(
      buildSpurUpdatePayload({
        action: "Reassign",
        siteCode,
        modifiedBy,
        palletGroupIds: pgIds,
        spurIds: [sourceSpurKey],
        newSpurIds: [targetSpurKey],
      }),
    );
    const refreshed = await fetchAssignmentWorkspace({ siteCode });
    const movedPgs = resolveMovedPgsFromWorkspace(
      refreshed,
      sourceSpurKey,
      targetSpurKey,
      pgIds,
    );
    return {
      ok: true,
      workspace: refreshed,
      movedPgs,
    };
  }

  const result = applyReassignToSpurs({
    assignmentsBySpur: workspace.spurAssignments,
    spurRows: workspace.spurSummary,
    sourceSpurKey,
    targetSpurKey,
    pgIds,
  });

  if (result.movedPgs.length === 0) {
    return { ok: false, error: "PG_NOT_FOUND_ON_SOURCE" };
  }

  return {
    ok: true,
    workspace: {
      spurAssignments: result.assignmentsBySpur,
      spurSummary: result.spurRows,
      unassignedPalletGroups: workspace.unassignedPalletGroups,
    },
    movedPgs: result.movedPgs,
  };
}

/**
 * @param {object} params
 * @param {AssignmentWorkspace} params.workspace
 * @param {string} params.sourceSpurKey
 * @param {string[]} params.pgIds
 */
export async function unassignPalletGroups(params) {
  const { workspace, sourceSpurKey, pgIds, siteCode, modifiedBy } = params;

  if (!shouldUseMockAssignmentActions(siteCode)) {
    await postSpurUpdate(
      buildSpurUpdatePayload({
        action: "Unassign",
        siteCode,
        modifiedBy,
        palletGroupIds: pgIds,
        spurIds: [sourceSpurKey],
      }),
    );
    const refreshed = await fetchAssignmentWorkspace({ siteCode });
    return {
      ok: true,
      workspace: refreshed,
      unassignedIds: pgIds,
    };
  }

  const result = applyUnassignFromSpur({
    assignmentsBySpur: workspace.spurAssignments,
    spurRows: workspace.spurSummary,
    sourceSpurKey,
    pgIds,
    unassignedPalletGroups: workspace.unassignedPalletGroups,
  });

  if (result.unassignedIds.length === 0) {
    return { ok: false, error: "PG_NOT_FOUND_ON_SOURCE" };
  }

  return {
    ok: true,
    workspace: {
      spurAssignments: result.assignmentsBySpur,
      spurSummary: result.spurRows,
      unassignedPalletGroups: result.unassignedPalletGroups,
    },
    unassignedIds: result.unassignedIds,
  };
}

/**
 * @param {object} params
 * @param {AssignmentWorkspace} params.workspace
 * @param {string[]} params.spurKeys
 */
function resolveMovedPgsFromWorkspace(workspace, sourceSpurKey, targetSpurKey, pgIds) {
  const idSet = new Set(pgIds);
  const targetSpur = workspace.spurSummary.find((row) => row.spur === targetSpurKey);
  if (targetSpur) {
    return getAssignedPalletGroupsForSpur(targetSpur, workspace.spurAssignments).filter(
      (pg) => idSet.has(pg.id),
    );
  }
  const sourceSpur = workspace.spurSummary.find((row) => row.spur === sourceSpurKey);
  if (sourceSpur) {
    return getAssignedPalletGroupsForSpur(sourceSpur, workspace.spurAssignments).filter(
      (pg) => idSet.has(pg.id),
    );
  }
  return pgIds.map((id) => ({ id, palletGroup: id }));
}

export async function completeSpurs(params) {
  const { workspace, spurKeys, siteCode, modifiedBy, forceComplete = false } = params;

  if (!shouldUseMockAssignmentActions(siteCode)) {
    const keySet = new Set(spurKeys);
    const spursToComplete = workspace.spurSummary.filter(
      (row) => keySet.has(row.spur) && (row.palletGroupCount || 0) > 0,
    );

    for (const spurRow of spursToComplete) {
      const pgIds = getAssignedPalletGroupsForSpur(
        spurRow,
        workspace.spurAssignments,
      ).map((pg) => pg.id);
      if (pgIds.length === 0) {
        continue;
      }
      const useForce =
        forceComplete || !spurRow.readyToComplete;
      await postSpurUpdate(
        buildSpurUpdatePayload({
          action: "Complete",
          siteCode,
          modifiedBy,
          palletGroupIds: pgIds,
          spurIds: [spurRow.spur],
          forceComplete: useForce,
        }),
      );
    }

    const refreshed = await fetchAssignmentWorkspace({ siteCode });
    return { ok: true, workspace: refreshed };
  }

  const keySet = new Set(spurKeys);
  const spurSummary = workspace.spurSummary.map((row) => {
    if (!keySet.has(row.spur) || (row.palletGroupCount || 0) <= 0) {
      return row;
    }
    if (!forceComplete && !row.readyToComplete) {
      return row;
    }
    return {
      ...row,
      spurStatus: SPUR_DISPLAY_STATUS.IDLE,
      readyToComplete: false,
      palletGroup: "—",
      palletGroupCount: 0,
      inboundId: "—",
      shipTo: "—",
      totalCartons: 0,
      diverted: 0,
      palletised: 0,
    };
  });

  const spurAssignments = { ...workspace.spurAssignments };
  spurKeys.forEach((key) => {
    spurAssignments[key] = [];
  });

  return {
    ok: true,
    workspace: {
      ...workspace,
      spurSummary,
      spurAssignments,
    },
  };
}

/** @returns {AssignmentWorkspace} */
export function buildWorkspaceSnapshot({
  unassignedPalletGroups,
  spurSummary,
  spurAssignments,
}) {
  return cloneWorkspaceState({
    unassignedPalletGroups,
    spurSummary,
    spurAssignments,
  });
}

export function getApiErrorMessage(errorCode) {
  switch (errorCode) {
    case "INVALID_ASSIGNMENT":
      return "Could not assign — use one PG to many spurs, or many PGs to one spur.";
    case "PG_NOT_FOUND_ON_SOURCE":
      return "Selected pallet group(s) were not found on the source spur.";
    default:
      return "Something went wrong. Please try again.";
  }
}

/** @param {unknown} error */
export function getHttpErrorMessage(error) {
  const status = error?.response?.status;
  const apiMessage = error?.response?.data?.message;

  if (status === 401) {
    return "Sign in with Okta to load data (API returned 401). If using REACT_APP_SKIP_OAUTH, the local API still requires a bearer token.";
  }
  if (status === 403) {
    return "Access denied (403). Sign out, sign in again, and confirm your access token includes node.ext.pallet.group read/update scopes. If it still fails, restart the backend (dev profile) after pulling latest service changes.";
  }
  if (status === 409) {
    return apiMessage
      ? `Update conflict: ${apiMessage}`
      : "Update conflict — refresh the page and try again.";
  }
  if (status === 400) {
    return apiMessage
      ? `Invalid request: ${apiMessage}`
      : "Invalid pallet group update request.";
  }
  if (status >= 500) {
    return apiMessage
      ? `Pallet group service error: ${apiMessage}`
      : "Pallet group service error. Refresh AWS credentials (aws sso login) and restart the backend.";
  }
  if (error?.code === "ERR_NETWORK") {
    return `Cannot reach pallet group API at ${config.palletGroupApi}. Is bootRun running on port 8080?`;
  }
  if (apiMessage) {
    return String(apiMessage);
  }
  return "Failed to load pallet group assignment data.";
}
